import { UnleashClient, type IMutableContext } from "unleash-proxy-client";
import type { FeatureFlagService, IContext } from "../types";

type QueueItem = () => Promise<void>;

export class UnleashBrowser<T extends string> implements FeatureFlagService {
  private client: UnleashClient;
  private readyPromise: Promise<void> | null = null;
  private context: IContext = {};
  private isContextUpdated: boolean = false;
  private userId: string | undefined;

  private queue: QueueItem[] = [];
  private isProcessingQueue: boolean = false;

  constructor(
    protected url: string,
    protected clientKey: string,
    protected appName: string,
  ) {
    this.client = this.getClientInstance();

    this.readyPromise = new Promise((resolve, reject) => {
      this.client.on("ready", () => {
        resolve();
      });
      this.client.on("error", (e: any) => {
        reject(e);
      });
    });
  }

  async connect(): Promise<void> {
    await this.client.start();
    await this.readyPromise;
  }

  async isEnabled(flag: T): Promise<boolean> {
    try {
      await this.readyPromise;
    } catch {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      this.enqueue(async () => {
        if (this.isContextUpdated) {
          await this.refreshContext();
        }

        const result = this.client.isEnabled(flag);
        resolve(result);
      });
    });
  }

  getContext(): IContext {
    return this.context;
  }

  setContextField(field: keyof IContext, value?: string): void {
    this.context = {
      ...this.context,
      [field]: value,
    };
    this.isContextUpdated = true;
  }

  getUserId(): string | undefined {
    return this.userId;
  }

  identify(userId?: string): void {
    this.userId = userId;
    this.isContextUpdated = true;
  }

  protected getClientInstance(): UnleashClient {
    return new UnleashClient({
      url: this.url,
      clientKey: this.clientKey,
      appName: this.appName,
      disableRefresh: true,
    });
  }

  private async refreshContext(): Promise<void> {
    const newContext = this.getParsedContext();
    await this.client.updateContext(newContext);
    this.isContextUpdated = false;
  }

  private getParsedContext(): IMutableContext {
    const context: IMutableContext = {};

    if (this.userId) {
      context.userId = this.userId;
    }

    const contextKeys = Object.keys(this.context);

    if (contextKeys.length) {
      contextKeys.forEach((key) => {
        if (!context.properties) {
          context.properties = {};
        }

        const typedKey = key as keyof IContext;

        context.properties[typedKey] = this.context[typedKey] as string;
      });
    }

    return context;
  }

  private async processQueue() {
    if (this.isProcessingQueue) return;

    this.isProcessingQueue = true;

    while (this.queue.length > 0) {
      const queueItem = this.queue.shift();
      if (queueItem) {
        await queueItem();
      }
    }

    this.isProcessingQueue = false;
  }

  private enqueue(item: QueueItem) {
    this.queue.push(item);
    this.processQueue();
  }
}
